import React, { useState, useEffect } from "react";
import { useGame } from "@/contexts/GameContext";
import { Button } from "@/components/ui/button";
import { Heart, Sword, Shield, Sparkles, ChevronUp, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import soundManager from "@/utils/SoundManager";
import { t } from "@/utils/i18n";

const STAT_OPTIONS = [
  { key: "hp", icon: Heart, color: "text-red-400", bg: "bg-red-400/10 border-red-400/30 hover:border-red-400/60" },
  { key: "base_attack", icon: Sword, color: "text-[#cfb53b]", bg: "bg-[#cfb53b]/10 border-[#cfb53b]/30 hover:border-[#cfb53b]/60" },
  { key: "base_defense", icon: Shield, color: "text-blue-400", bg: "bg-blue-400/10 border-blue-400/30 hover:border-blue-400/60" },
  { key: "max_mana", icon: Sparkles, color: "text-purple-400", bg: "bg-purple-400/10 border-purple-400/30 hover:border-purple-400/60" },
];

const STAT_LABELS = {
  hp: "stat_hp", base_attack: "stat_atk", base_defense: "stat_def", max_mana: "stat_mana"
};

const STAT_BOOST = { hp: "+3 HP", base_attack: "+1 ATK", base_defense: "+1 DEF", max_mana: "+1 Mana" };

export default function LevelUpModal() {
  const { game, allocateStat, getEvolutions, evolveClass } = useGame();
  const [allocating, setAllocating] = useState(null);
  const [evolutions, setEvolutions] = useState(null);
  const [evolving, setEvolving] = useState(null);
  const [showEvolution, setShowEvolution] = useState(false);

  const char = game?.character;
  const lang = game?.language || "en";
  const statPoints = char?.stat_points || 0;
  const evolutionAvailable = char?.evolution_available;

  useEffect(() => {
    if (evolutionAvailable && game?.id) {
      getEvolutions(game.id).then(data => {
        if (data.options?.length > 0) setEvolutions(data.options);
      }).catch(() => {});
    }
  }, [evolutionAvailable, game?.id]);

  const handleAllocate = async (stat) => {
    if (!game?.id || allocating) return;
    setAllocating(stat);
    try {
      await allocateStat(game.id, stat);
      soundManager.playNewItem();
      toast.success(t(lang, 'stat_allocated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setAllocating(null);
    }
  };

  const handleEvolve = async (evoName) => {
    if (!game?.id || evolving) return;
    setEvolving(evoName);
    try {
      const result = await evolveClass(game.id, evoName);
      soundManager.playVictory();
      toast.success(`${t(lang, 'evolution_complete')} ${result.new_class}`);
      setShowEvolution(false);
      setEvolutions(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setEvolving(null);
    }
  };

  if (statPoints <= 0 && !evolutionAvailable) return null;

  return (
    <>
      {/* Stat Points Banner */}
      {statPoints > 0 && (
        <div className="mx-2 sm:mx-4 mb-2 bg-gradient-to-r from-[#cfb53b]/10 to-[#cfb53b]/5 border border-[#cfb53b]/30 rounded-lg p-3 animate-fadeInUp" data-testid="stat-points-banner">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-heading text-sm text-[#cfb53b] flex items-center gap-1.5">
              <ChevronUp size={14} /> {t(lang, 'allocate_stat')}
            </h4>
            <span className="text-xs bg-[#cfb53b]/20 text-[#cfb53b] px-2 py-0.5 rounded-full font-heading">
              {statPoints} {t(lang, 'stat_points')}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {STAT_OPTIONS.map(opt => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  data-testid={`allocate-${opt.key}`}
                  onClick={() => handleAllocate(opt.key)}
                  disabled={!!allocating}
                  className={`border rounded-lg p-2 text-center transition-all cursor-pointer ${opt.bg}`}
                >
                  {allocating === opt.key ? (
                    <Loader2 size={16} className="mx-auto animate-spin text-[#cfb53b]" />
                  ) : (
                    <Icon size={16} className={`mx-auto ${opt.color} mb-1`} />
                  )}
                  <p className="text-[10px] text-[#e2e8f0] font-heading">{t(lang, STAT_LABELS[opt.key])}</p>
                  <p className="text-[9px] text-[#94a3b8]">{STAT_BOOST[opt.key]}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Evolution Banner */}
      {evolutionAvailable && evolutions && !showEvolution && (
        <div className="mx-2 sm:mx-4 mb-2 bg-gradient-to-r from-purple-500/10 to-[#cfb53b]/10 border border-purple-500/30 rounded-lg p-3 animate-fadeInUp" data-testid="evolution-banner">
          <div className="flex items-center justify-between">
            <h4 className="font-heading text-sm text-purple-300 flex items-center gap-1.5">
              <Crown size={14} /> {t(lang, 'evolution_available')}
            </h4>
            <Button
              data-testid="open-evolution"
              onClick={() => setShowEvolution(true)}
              size="sm"
              className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30 font-heading text-xs h-7"
            >
              {t(lang, 'choose_evolution')}
            </Button>
          </div>
        </div>
      )}

      {/* Evolution Modal */}
      {showEvolution && evolutions && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="evolution-modal">
          <div className="bg-[#1a1a1d] border border-purple-500/30 rounded-xl p-5 max-w-lg w-full animate-fadeInUp shadow-[0_0_40px_rgba(147,51,234,0.15)]">
            <h3 className="font-heading text-lg text-[#cfb53b] text-center mb-1 flex items-center justify-center gap-2">
              <Crown size={20} className="text-purple-400" /> {t(lang, 'choose_evolution')}
            </h3>
            <p className="text-xs text-[#94a3b8] text-center mb-4">
              {char?.class_type} Lv.{char?.level}
            </p>
            <div className="space-y-3">
              {evolutions.map(evo => (
                <div
                  key={evo.class_name}
                  className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-4 hover:border-purple-500/40 transition-all"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-heading text-sm text-purple-300">{evo.class_name}</h4>
                      <p className="text-[10px] text-[#94a3b8]">{evo.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2 text-[10px]">
                      {Object.entries(evo.bonus).map(([k, v]) => (
                        <span key={k} className={`px-1.5 py-0.5 rounded ${
                          k.includes("attack") ? "bg-[#cfb53b]/10 text-[#cfb53b]" :
                          k.includes("defense") ? "bg-blue-400/10 text-blue-400" :
                          k.includes("hp") ? "bg-red-400/10 text-red-400" :
                          "bg-purple-400/10 text-purple-400"
                        }`}>
                          +{v} {k.replace("max_", "").replace("base_", "").toUpperCase()}
                        </span>
                      ))}
                    </div>
                    <Button
                      data-testid={`evolve-${evo.class_name}`}
                      onClick={() => handleEvolve(evo.class_name)}
                      disabled={!!evolving}
                      size="sm"
                      className="bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40 font-heading text-xs h-7"
                    >
                      {evolving === evo.class_name ? <Loader2 size={12} className="animate-spin" /> : t(lang, 'evolve')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <Button
              variant="ghost"
              onClick={() => setShowEvolution(false)}
              className="w-full mt-3 text-[#94a3b8] text-xs"
            >
              {t(lang, 'cancel')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
